import { Injectable } from '@angular/core';
import { catchError, of, throwError } from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { UpdateLmsCoursePayload } from '../../types/payloads/lms-course.payloads';
import { LmsCourseAPIResponse } from '../../types/responses/lms-course.responses';
import { AlertService, AlertType } from '../alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class LMSCourseService {
  constructor(
    private apiService: APICoreService,
    private alertService: AlertService
  ) {}

  createCourse(courseFile: string) {
    return this.apiService
      .postRequest('lms-course', {
        course_file: courseFile,
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  getCourse(courseId: number, expand = '') {
    return this.apiService.getRequest(
      `lms-course/${courseId}?expand=${expand}`
    );
  }

  createCourseRegistration(courseId: number, user_id: number) {
    return this.apiService.postRequest(`lms-course/${courseId}/register`, {
      user_id,
    });
  }

  getUserCourseRegistration(courseId: number, userId: number) {
    return this.apiService.getRequest(
      `lms-course/${courseId}/user-registration/${userId}`
    );
  }

  getCourseRegistrations(courseId: number, pageNumber = 1) {
    return this.apiService.getRequest(
      `lms-course/${courseId}/registrations?expand=user.profile&page=${pageNumber}`
    );
  }

  updateCourse(courseId: number, payload: UpdateLmsCoursePayload) {
    this.apiService.putRequest(`lms-course/${courseId}`, payload).subscribe({
      error: (error) => {
        this.alertService.showAlert(
          error.error.message,
          undefined,
          AlertType.DANGER
        );
      },
      next: (response) => {
        if (response.item) {
          this.alertService.showAlert('Course Updated');
        }
      },
    });
  }

  deleteCourse(courseId: number) {
    return this.apiService
      .deleteRequest(`lms-course/${courseId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  // SHARING
  handleShareDistricts(
    course: LmsCourseAPIResponse,
    incomingDistricts: DistrictSimpleDto[]
  ) {
    const incomingDistrictIds = incomingDistricts.map(
      (district) => district.id
    );
    if (course?.districtsSharedWith) {
      // Handle districts to be deleted (those in form.districtsSharedWith but not in incomingDistricts)
      course.districtsSharedWith.forEach((share) => {
        if (course && !incomingDistrictIds.includes(share.district_id)) {
          this.deleteCourseShare(course.id, share.id).subscribe({
            error: (error) => {
              this.alertService.showAlert(
                error.error.message,
                undefined,
                AlertType.DANGER
              );
            },
          });
        }
      });

      // Handle new districts to be added (those in incomingDistricts but not in session.sharing)
      incomingDistricts.forEach((district) => {
        // Check if the district is not already in this.form.districtsSharedWith
        if (
          course &&
          !course.districtsSharedWith?.some(
            (share) => share.district_id === district.id
          )
        ) {
          // If it's not found, we proceed to share it
          this.shareCourse(course.id, district.id).subscribe({
            error: (error) => {
              this.alertService.showAlert(
                error.error.message,
                undefined,
                AlertType.DANGER
              );
            },
          });
        }
      });
    }
    return of(incomingDistrictIds);
  }

  handleExcludeDistricts(
    course: LmsCourseAPIResponse,
    incomingDistricts: DistrictSimpleDto[]
  ) {
    const incomingDistrictIds = incomingDistricts.map(
      (district) => district.id
    );
    if (course.districtsExcludedFrom) {
      // Handle districts to be deleted (those in session.sharing but not in incomingDistricts)
      course.districtsExcludedFrom.forEach((share) => {
        if (
          !share.is_shared &&
          !incomingDistrictIds.includes(share.district_id)
        ) {
          if (course) {
            this.deleteCourseShare(course.id, share.id).subscribe({
              error: (error) => {
                this.alertService.showAlert(
                  error.error.message,
                  undefined,
                  AlertType.DANGER
                );
              },
            });
          }
        }
      });
      // Handle new districts to be added (those in incomingDistricts but not in session.sharing)
      incomingDistricts.forEach((district) => {
        if (
          course &&
          !course.districtsExcludedFrom?.some(
            (share) => share.district_id === district.id
          )
        ) {
          this.unshareCourse(course.id, district.id).subscribe({
            error: (error) => {
              this.alertService.showAlert(
                error.error.message,
                undefined,
                AlertType.DANGER
              );
            },
          });
        }
      });
    }

    return of(incomingDistrictIds);
  }

  // REMOVE WHEN DONE
  shareCourse(courseId: number, districtId: number) {
    return this.apiService
      .postRequest(`lms-course/${courseId}/share/${districtId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  unshareCourse(courseId: number, districtId: number) {
    return this.apiService
      .postRequest(`lms-course/${courseId}/unshare/${districtId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteCourseShare(courseId: number, shareId: number) {
    return this.apiService
      .deleteRequest(`lms-course/${courseId}/share/${shareId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }
}
